import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, CheckCircle, X, Trash2, Check, Loader2, Clock } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { formatDateTime } from '../../utils/timeUtils';

interface Message {
  id: string;
  title: string;
  content: string;
  is_read: boolean;
  created_at: string;
  read_at?: string;
  sender_type: string;
}

export default function TeacherMessages() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const session = await supabase.auth.getSession();
    const response = await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.data.session?.access_token}`,
      },
      body: JSON.stringify({ action: 'messages_get_messages', userId: user.id }),
    });

    const result = await response.json();
    if (result.success) {
      setMessages(result.data || []);
    }
    setLoading(false);
  };

  const markAsRead = async (messageId: string) => {
    setActionLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const session = await supabase.auth.getSession();
    await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.data.session?.access_token}`,
      },
      body: JSON.stringify({ action: 'messages_mark_as_read', messageId }),
    });

    setMessages(prev => prev.map(m => m.id === messageId ? { ...m, is_read: true, read_at: new Date().toISOString() } : m));
    setActionLoading(false);
  };

  const markAllAsRead = async () => {
    setActionLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const session = await supabase.auth.getSession();
    await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.data.session?.access_token}`,
      },
      body: JSON.stringify({ action: 'messages_mark_all_read', userId: user.id }),
    });

    setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
    setActionLoading(false);
  };

  const deleteMessage = async (messageId: string) => {
    setActionLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const session = await supabase.auth.getSession();
    await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.data.session?.access_token}`,
      },
      body: JSON.stringify({ action: 'messages_delete', messageId }),
    });

    setMessages(prev => prev.filter(m => m.id !== messageId));
    setActionLoading(false);
  };

  const unreadCount = (messages || []).filter(m => !m.is_read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Mail className="w-6 h-6 text-amber-600" />
          站内信
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
              未读 {unreadCount} 条
            </span>
          )}
        </h1>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            disabled={actionLoading}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-amber-700 hover:bg-amber-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <Check className="w-4 h-4" />
            全部标已读
          </button>
        )}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100"
      >
        {messages.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                  !message.is_read ? 'bg-amber-50/50' : ''
                }`}
                onClick={() => {
                  setSelectedMessage(message);
                  if (!message.is_read) markAsRead(message.id);
                }}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    message.is_read ? 'bg-gray-100' : 'bg-amber-100'
                  }`}>
                    <Mail className={`w-5 h-5 ${message.is_read ? 'text-gray-500' : 'text-amber-600'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-medium truncate ${message.is_read ? 'text-gray-700' : 'text-gray-900'}`}>
                        {message.title}
                      </h3>
                      {!message.is_read && (
                        <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-1">{message.content}</p>
                    <div className="flex items-center gap-1 mt-2 text-xs text-gray-400">
                      <Clock className="w-3 h-3" />
                      {formatDateTime(message.created_at)}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMessage(message.id);
                    }}
                    disabled={actionLoading}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-amber-600" />
            </div>
            <p className="text-gray-500">暂无消息</p>
            <p className="text-sm text-gray-400 mt-1">系统消息将在这里显示</p>
          </div>
        )}
      </motion.div>

      <AnimatePresence>
        {selectedMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedMessage(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-lg overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-800">{selectedMessage.title}</h3>
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4">
                <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.content}</p>
                <div className="flex items-center gap-2 mt-4 text-sm text-gray-400">
                  <Clock className="w-4 h-4" />
                  {formatDateTime(selectedMessage.created_at)}
                  {selectedMessage.is_read && (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-500 ml-2" />
                      <span className="text-green-600">已读</span>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
